import { NavHeader } from "@/components/layout/nav-header";
import { useLocation } from "wouter";
import { ShimmeringDisc } from "@/components/ui/shimmering-disc";
import { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";

export default function LandingPage() {
  const [, setLocation] = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [isOpen]);

  return (
    <div className="min-h-screen bg-clubhouse-black text-white">
      <NavHeader />

      <main className="container mx-auto px-6 max-w-2xl min-h-[calc(100vh-4rem)] flex flex-col justify-center">
        {/* Hero */}
        <div className="flex flex-col items-center pb-12 animate-stagger">
          <div className="mt-5">
            <ShimmeringDisc size={200} />
          </div>

          <h1 className="mt-1 text-[28px] font-semibold text-white/95 text-center leading-tight">
            Book your set
          </h1>
          <p className="mt-3 text-[18px] text-white/40 text-center max-w-sm leading-relaxed">
            Submit a request to perform on<br className="sm:hidden" /> Clubhouse FM.
          </p>

          <button
            onClick={() => setLocation("/booking/new")}
            className="mt-8 px-8 py-2.5 rounded-full bg-white text-black text-[18px] font-medium hover:bg-white/90 active:scale-[0.97] active:bg-white/80 transition-all"
          >
            New booking
          </button>
        </div>

        {/* Info - Collapsible */}
        <div className="rounded-2xl bg-white/[0.04] border border-white/[0.08] overflow-hidden hover-lift">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="w-full p-6 sm:p-8 flex items-center justify-between gap-4 text-left hover:bg-white/[0.02] transition-colors"
          >
            <div>
              <h2 className="text-[18px] font-medium text-white/90">About Clubhouse FM</h2>
              <p className="mt-1 text-[18px] text-white/30">
                A not-for-profit community project centered on music &amp; culture.
                <br className="sm:hidden" />
                <span className="sm:hidden text-white/50 underline underline-offset-2">Learn more</span>
              </p>
            </div>
            <ChevronDown
              className="h-5 w-5 text-white/30 shrink-0 transition-transform duration-300 ease-out hidden sm:block"
              style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}
            />
          </button>

          <div
            style={{ height: isOpen ? contentHeight : 0 }}
            className="transition-[height] duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] overflow-hidden"
          >
            <div ref={contentRef} className="px-6 sm:px-8 pb-6 sm:pb-8">
              <div className="border-t border-white/[0.06] pt-6 space-y-4 text-[18px] text-white/40 leading-relaxed">
                <p>
                  Clubhouse FM started as a nonprofit project dedicated to raising funds and awareness
                  to help those in need. We raised over $100k to help those affected by the devastating
                  LA wild fires of 2025. And that ethos remains at our core.
                </p>
                <p>
                  We are now a community-driven radio station in Los Angeles providing DJs and artists
                  with world-class streaming infrastructure and a creative production space.
                </p>
                <p>
                  Through our partnership with YouTube, we reach a global audience and help artists
                  extend their style beyond LA.
                </p>
                <p className="italic text-white/30">
                  You can dedicate your set to a cause or simply contribute to the culture.
                  Either way, you're part of the community.
                </p>

                <p className="pt-2 text-[14px] text-white/20 flex items-center gap-1.5">
                  <span className="text-white/60 animate-pulse">&#x26A1;</span>
                  Powered by Afters Club &amp; Friends
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="h-16" />
      </main>
    </div>
  );
}
